import { ErrorNotice, formatDateTime } from "../components/ui";
import { describeMovement, formatDelta, movementActor } from "./labels";
import { useMovementHistory } from "./useInventory";

/**
 * Why this item's stock is what it is.
 *
 * Loaded ON DEMAND, when a row's drawer is opened. A history request per row
 * would be a query per line of a table for a panel nobody has looked at.
 *
 * WHAT IS DELIBERATELY NOT A COLUMN
 * A running balance. It could only be reconstructed backwards from today's
 * quantity, and any movement older than the inventory system's own opening
 * balance would make that arithmetic a fiction — a number that looks like
 * evidence and is not. What every row DOES carry is the signed delta, which is
 * what actually happened.
 *
 * No email address appears here and none is sent. An administrator is
 * identified by their display name, resolved from the account id the ledger
 * recorded, and a movement a customer's own order caused is attributed to the
 * order rather than to a person.
 */
export function MovementHistoryPanel({
  itemId,
  itemLabel,
}: {
  itemId: string;
  itemLabel: string;
}) {
  const history = useMovementHistory(itemId);

  return (
    <div className="inventory__history" data-testid="movement-history">
      {history.isPending ? (
        <div className="empty" role="status">
          Loading history…
        </div>
      ) : history.isError ? (
        <div className="inventory__historyFailure">
          <ErrorNotice error={history.error} />
          <button
            type="button"
            className="btn btn--sm"
            disabled={history.isFetching}
            onClick={() => void history.refetch()}
          >
            {history.isFetching ? "Retrying…" : "Retry"}
          </button>
        </div>
      ) : history.data && history.data.events.length === 0 ? (
        <div className="empty">
          No stock has moved for {itemLabel} yet.
        </div>
      ) : history.data ? (
        <>
          <table className="table table--compact">
            <caption className="sr-only">
              Stock movements for {itemLabel}, newest first
            </caption>
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col" className="num">
                  Change
                </th>
                <th scope="col">What happened</th>
                <th scope="col">Note</th>
                <th scope="col">Who</th>
              </tr>
            </thead>
            <tbody>
              {history.data.events.map((event) => (
                <tr key={`${event.at}-${event.delta}-${event.reason}`}>
                  <td className="muted">{formatDateTime(event.at)}</td>
                  <td
                    className={
                      event.delta > 0
                        ? "num inventory__delta is-up"
                        : "num inventory__delta is-down"
                    }
                  >
                    {formatDelta(event.delta)}
                  </td>
                  <td>{describeMovement(event)}</td>
                  <td className="muted">{event.note ?? "—"}</td>
                  <td className="muted">{movementActor(event)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {history.data.count >= history.data.limit ? (
            <p className="muted">
              Showing the {history.data.limit} most recent movements.
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
